import { useState, FC, ReactNode, useRef, useEffect, useCallback } from 'react';
import { Icon, tailwindOverride, Tooltip } from '@getsynapse/design-system';
import { EditableTableRowCell } from '../EditableTableRowCell';
import { BaseCellProps } from '../types';
import {
  Option,
  PilotPagesToTrack,
  StatusInlineEditingEntityType,
} from 'utils/customTypes';
import { StatusDropdown } from './StatusDropdown';
import { USERPILOT_EVENTS } from 'utils/constants';
import { Userpilot } from 'userpilot';
import { useTooltip } from '../hooks';
import intl from 'react-intl-universal';

export type StatusOption = Option & { disabled?: boolean; message?: string };

interface StatusTableRowCellProps extends Omit<BaseCellProps, 'value'> {
  value: string;
  customizedValue: (value: string) => ReactNode;
  options: StatusOption[];
  placeholder?: string;
  isTaskProjectDisabled?: boolean;
  canInlineEditTask?: boolean;
  canEdit?: boolean;
  userPilotEventOrigin?: PilotPagesToTrack;
  entityType?: StatusInlineEditingEntityType;
  isArchived?: boolean;
}

export const StatusTableRowCell: FC<StatusTableRowCellProps> = ({
  value,
  customizedValue,
  options,
  isEditable,
  className = '',
  onSave,
  onCancel,
  isTaskProjectDisabled,
  canInlineEditTask,
  canEdit,
  userPilotEventOrigin,
  entityType = StatusInlineEditingEntityType.TASK,
  isArchived,
}) => {
  const [editValue, setEditValue] = useState(value);
  const toggleDropdownRef = useRef<(() => void) | null>(null);
  const dropdownTriggerRef = useRef<HTMLDivElement | null>(null);
  const saveTriggerRef = useRef<(() => void) | null>(null);
  const pendingSaveValueRef = useRef<string | null>(null);

  const { tooltipRef } = useTooltip();

  useEffect(() => {
    setEditValue(value);
  }, [value]);

  const selectedOption = options.find((option) => option.value === value);
  const hoverStateStyles = tailwindOverride(
    'border border-transparent bg-white hover:border-cool-mist group-focus-visible:border-cool-mist rounded flex items-center justify-between p-2 ml-1'
  );
  const fieldChildren = (
    <>
      {customizedValue(value)}
      <Icon name='caret-down-outline' />
    </>
  );

  const getHoverField = () => {
    if (
      !canInlineEditTask ||
      isTaskProjectDisabled ||
      !canEdit ||
      (entityType === StatusInlineEditingEntityType.PROJECT && isArchived)
    ) {
      return (
        <div
          className={tailwindOverride(
            'cursor-auto',
            hoverStateStyles,
            'border-cool-mist bg-off-white'
          )}
          aria-disabled={true}
        >
          {fieldChildren}
        </div>
      );
    }

    return (
      <div className={tailwindOverride('cursor-pointer', hoverStateStyles)}>
        {fieldChildren}
      </div>
    );
  };

  const hoverInputChildren = getHoverField();

  const readModeChildren = (
    <div className='border border-transparent flex items-center justify-between p-2 ml-1'>
      {customizedValue(value)}
      <span className='opacity-0' aria-hidden='true'>
        <Icon name='caret-down-outline' />
      </span>
    </div>
  );

  const handleSave = () => {
    const valueToSave = pendingSaveValueRef.current ?? editValue;
    pendingSaveValueRef.current = null;
    onSave?.(valueToSave);

    const userpilotEventMap: Record<StatusInlineEditingEntityType, string> = {
      [StatusInlineEditingEntityType.PROJECT]:
        USERPILOT_EVENTS.PROJECT_STATUS_INLINE_EDITING,
      [StatusInlineEditingEntityType.PROGRAM]:
        USERPILOT_EVENTS.PROGRAM_STATUS_INLINE_EDITING,
      [StatusInlineEditingEntityType.TASK]:
        USERPILOT_EVENTS.TASK_STATUS_INLINE_EDITING,
    };
    const userpilotEvent =
      userpilotEventMap[entityType] ||
      USERPILOT_EVENTS.TASK_STATUS_INLINE_EDITING;

    try {
      Userpilot.track(userpilotEvent, {
        eventOrigin: userPilotEventOrigin,
        newValue: valueToSave,
        oldValue: value,
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(
        `Error tracking Userpilot event for edit ${entityType} status`,
        error
      );
    }
  };

  const handleCancel = () => {
    setEditValue(value);
    pendingSaveValueRef.current = null;
    onCancel?.();
  };

  const onChange = useCallback((newValue: string) => {
    setEditValue(newValue);
    pendingSaveValueRef.current = newValue;
    saveTriggerRef.current?.();
  }, []);

  const getTooltipMessage = () => {
    if (isTaskProjectDisabled)
      return intl.get('TASKS.PROJECT_DISABLED_TOOLTIP');
    if (!canInlineEditTask) return intl.get('TASKS.TASK_DISABLED_TOOLTIP');
    if (!canEdit) {
      if (entityType === StatusInlineEditingEntityType.PROJECT) {
        return intl.get('PROJECT_DETAIL.NO_EDIT_PERMISSION');
      }
      if (entityType === StatusInlineEditingEntityType.PROGRAM) {
        return intl.get('PROGRAM_PAGE.NO_EDIT_PERMISSION');
      }
      return intl.get('TASKS.NO_EDIT_PERMISSION');
    }
    if (entityType === StatusInlineEditingEntityType.PROJECT && isArchived) {
      return intl.get('PROJECT_DETAIL.ARCHIVED_STATUS_DISABLED');
    }

    return '';
  };

  const tooltipMessage = getTooltipMessage();

  const editModeChildren = (
    <StatusDropdown
      value={editValue}
      customizedValue={customizedValue}
      selectedOption={selectedOption ? [selectedOption] : []}
      options={options}
      onChange={onChange}
      toggleDropdownRef={toggleDropdownRef}
      triggerRef={dropdownTriggerRef}
      autoOpen
      saveTriggerRef={saveTriggerRef}
    />
  );

  return (
    <Tooltip
      ref={tooltipRef}
      openMode='hover1'
      timeout={0}
      showPopper
      position='topCenter'
      contentProps={{
        className: 'px-3 py-2 text-sm z-50 absolute',
      }}
      className='w-full'
      usePortal
      trigger={
        <div className='w-full'>
          <EditableTableRowCell
            isEditable={!!isEditable}
            editModeChildren={editModeChildren}
            readModeChildren={readModeChildren}
            hoverInputChildren={hoverInputChildren}
            showHoverInput={true}
            className={className}
            onSave={handleSave}
            onCancel={handleCancel}
            saveTriggerRef={saveTriggerRef}
          />
        </div>
      }
    >
      {tooltipMessage}
    </Tooltip>
  );
};
